import { Fragment, useEffect } from "react";
import { Container } from "../Container/Container";
import { ClassSchedule } from "./data";
import { subjectTone } from "./subjectTone";
import {
  Cell,
  CellNote,
  CellTime,
  CornerCell,
  EmptyCell,
  Grid,
  GridScroll,
  Head,
  HeadClass,
  HeadCell,
  HeadMeta,
  Legend,
  MobileCard,
  MobileCardHead,
  MobileCardMeta,
  MobileCardTitle,
  MobileCell,
  MobileEmpty,
  MobileList,
  MobileMeta,
  MobileRow,
  MobileRows,
  MobileSubject,
  MobileTime,
  Page,
  SlotLabel,
  Subject,
  Subtitle,
  TimeCell,
  Title,
} from "./styled";

export const ClassScheduleView = ({ schedule }: { schedule: ClassSchedule }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { title, subtitle, cornerLabel = "Время", columns, rows, legend } = schedule;

  return (
    <Container>
      <Page>
        <Head>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Head>

        <GridScroll>
          <Grid $columns={columns.length}>
            <CornerCell>{cornerLabel}</CornerCell>
            {columns.map((column, index) => (
              <HeadCell key={`${column.name}-${index}`}>
                <HeadClass>{column.name}</HeadClass>
                {column.teacher && <HeadMeta>{column.teacher}</HeadMeta>}
                <HeadMeta>{column.room}</HeadMeta>
              </HeadCell>
            ))}

            {rows.map((row) => (
              <Fragment key={row.time}>
                <TimeCell>{row.time}</TimeCell>
                {columns.map((column, index) => {
                  const lesson = row.lessons[index];
                  if (!lesson) {
                    return <EmptyCell key={`${row.time}-${index}`} />;
                  }
                  return (
                    <Cell
                      key={`${row.time}-${index}`}
                      $tone={column.tone ?? subjectTone(lesson.subject)}
                    >
                      {lesson.label && <SlotLabel>{lesson.label}</SlotLabel>}
                      <Subject>{lesson.subject}</Subject>
                      {lesson.time && <CellTime>{lesson.time}</CellTime>}
                      {lesson.room && <CellNote>{lesson.room}</CellNote>}
                      {lesson.note && <CellNote>{lesson.note}</CellNote>}
                    </Cell>
                  );
                })}
              </Fragment>
            ))}
          </Grid>
        </GridScroll>

        <MobileList>
          {columns.map((column, index) => (
            <MobileCard key={`${column.name}-${index}`}>
              <MobileCardHead>
                <MobileCardTitle>{column.name}</MobileCardTitle>
                <MobileCardMeta>
                  {[column.teacher, column.room].filter(Boolean).join(" · ")}
                </MobileCardMeta>
              </MobileCardHead>
              <MobileRows>
                {rows.map((row) => {
                  const lesson = row.lessons[index];
                  const meta = lesson
                    ? [lesson.time, lesson.room, lesson.note].filter(Boolean).join(" · ")
                    : "";
                  return (
                    <MobileRow
                      key={`${column.name}-${row.time}`}
                      $tone={
                        lesson
                          ? column.tone ?? subjectTone(lesson.subject)
                          : "neutral"
                      }
                    >
                      <MobileTime>{row.time}</MobileTime>
                      {lesson ? (
                        <MobileCell>
                          <MobileSubject>
                            {lesson.label ? `${lesson.label} · ` : ""}
                            {lesson.subject}
                          </MobileSubject>
                          {meta && <MobileMeta>{meta}</MobileMeta>}
                        </MobileCell>
                      ) : (
                        <MobileEmpty>—</MobileEmpty>
                      )}
                    </MobileRow>
                  );
                })}
              </MobileRows>
            </MobileCard>
          ))}
        </MobileList>

        {legend && <Legend>{legend}</Legend>}
      </Page>
    </Container>
  );
};
